import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Col, Container, Row } from 'reactstrap';
import { getPowerBIDashboardEmbedUrl, exportToFileInGroup, getExportToFileStatusInGroup, getFileOfExportToFileInGroup, getNarrativeReport } from '../../../ApiUtils/ApiUtils';
import { service, factories, models, Report } from "powerbi-client";
import { AxiosResponse } from 'axios';
import { setReport } from "../../../store/reportStore";

const powerbi = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory);

type PowerBIDashboardProps = {
    dashboardKey: string;
    dashboardName?: string;
}

interface PowerBIDashboardState {
    isLoading: boolean;
    hasError: boolean;
    powerBiInfo: any | null;
    reportId: string | null;
    format: string;
    isExporting: boolean;
    exportButtonText: string;
    calculatedHeight: string;
    isDownloadingNarrative: boolean;
    narrativeButtonText: string;
}

class PowerBIDashboard extends React.Component<PowerBIDashboardProps, PowerBIDashboardState> {
    containerRef: React.RefObject<HTMLDivElement>;
    resizeObserver: ResizeObserver | null = null;
    report: Report | null = null;
    constructor(props: PowerBIDashboardProps) {
        super(props);
        this.containerRef = React.createRef();
        this.state = {
            isLoading: true,
            hasError: false,
            powerBiInfo: {},
            reportId: null,
            format: 'pdf',
            isExporting: false,
            exportButtonText: 'Export Report',
            calculatedHeight: '0px',
            isDownloadingNarrative: false,
            narrativeButtonText: 'Download AI Narrative',
        }
    }

    componentDidMount() {
        this.loadDashboardData(this.props.dashboardKey);
        this.calculateHeightFromWidth();

        const container = this.containerRef.current;
        if (container) {
            this.resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(() => {
                    this.calculateHeightFromWidth();
                });
            });
            this.resizeObserver.observe(container);
        }
    }

    componentDidUpdate(prevProps: PowerBIDashboardProps) {
        if (prevProps.dashboardKey !== this.props.dashboardKey) {
            this.setState({ isLoading: true, hasError: false });
            this.loadDashboardData(this.props.dashboardKey);
        }
    }

     componentWillUnmount() {
        if (this.resizeObserver && this.containerRef.current) {
            this.resizeObserver.unobserve(this.containerRef.current);
        }
        this.resizeObserver = null;
        this.report = null;
    }

    calculateHeightFromWidth = () => {
        const container = this.containerRef.current;
        if (container) {
            const width = container.offsetWidth;
            const newHeight = `${width * 1.35}px`;
            if (this.state.calculatedHeight !== newHeight) {
                this.setState({ calculatedHeight: newHeight });
            }
        }
    };

    loadDashboardData = (dashboardKey: string) => {
        this.report = null;
        getPowerBIDashboardEmbedUrl(dashboardKey).then((response: {
            data: {
                info: string,
                accessToken: any
            }
        }) => {
            var reportContainer = document.querySelector("#powerBiReportContainer");
            var embedData: any = JSON.parse(response.data.info);
            var embedUrl: string = embedData.EmbedReport[0].EmbedUrl;
            var reportId: string = embedData.EmbedReport[0].ReportId;

            setReport({ id: reportId, name: this.props.dashboardName || null });

            var reportLoadConfig = {
                type: "report",
                tokenType: models.TokenType.Embed,
                accessToken: embedData.EmbedToken.Token,
                embedUrl: embedUrl
            };

            this.report = powerbi.embed(reportContainer as HTMLElement, reportLoadConfig) as Report;
            this.setState({ reportId: reportId });
        }).catch((error) => {
            console.error('Loading failed:', error);
            // The embed config is bad or unreachable, so there is no report to ask about.
            // Tell the store so the assistant can explain itself rather than keeping the
            // previously loaded report selected behind the user's back.
            setReport({ id: null, name: this.props.dashboardName || null, failed: true });
            this.setState({ hasError: true });
        }).finally(() => {
            this.setState({ isLoading: false }); 
        });
    }


    captureBookmarkState = async (): Promise<string | undefined> => {
        if (!this.report) {
            return undefined;
        }
        try {
            const capturePromise = this.report.bookmarksManager.capture({ allPages: true });
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Bookmark capture timed out')), 5000)
            );
            const bookmark = await Promise.race([capturePromise, timeoutPromise]);
            return bookmark?.state || undefined;
        } catch (error) {
            console.warn('Bookmark capture failed; exporting default report view.', error);
            return undefined;
        }
    };

    handleExport = async (fileHandle: any) => {
        try {
            this.setState({
                isExporting: true,
                exportButtonText: "Progress: 0%"
            });

            // Capture the report's current on-screen filter/slicer/page state
            var bookmarkState = await this.captureBookmarkState();

            // Initiate export
            var exportResponse: AxiosResponse = await exportToFileInGroup(this.props.dashboardKey, this.state.format, bookmarkState);
            var exportId = exportResponse.data.export.id;
            var exportStatus = exportResponse.data.export.status;

            // Polling for export status
            while (exportStatus !== "Succeeded") {
                await new Promise(resolve => setTimeout(resolve, 15000));
                const exportStatusResponse: AxiosResponse = await getExportToFileStatusInGroup(this.props.dashboardKey, exportId);
                exportStatus = exportStatusResponse.data.exportStatus.status;
                this.setState({
                    isExporting: true,
                    exportButtonText: `Progress: ${exportStatusResponse.data.exportStatus.percentComplete}%`
                });
                if (exportStatus == "Failed") {
                    throw new Error("Export failed. Please try again later.");
                }
            }

            // Get exported file data
            var downloadResponse: AxiosResponse = await getFileOfExportToFileInGroup(this.props.dashboardKey, this.state.format, exportId);
            var contentType = String(downloadResponse.headers['content-type'] || 'application/octet-stream');
            var blob = new Blob([downloadResponse.data], { type: contentType });

            if (fileHandle) {
                // Write to the user-selected file
                this.setState({ exportButtonText: 'Saving...' });
                var writable = await fileHandle.createWritable();
                await writable.write(blob);
                await writable.close();
            } else {
                // Fallback: Trigger download automatically
                var link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = `report_${new Date().toLocaleDateString('en-CA')}.${this.state.format}`;
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Download failed:', error);
        } finally {
            this.setState({
                isExporting: false,
                exportButtonText: 'Export Report'
            });
        }
    };

    // User-triggered function for picking the file
    handleSaveAs = async () => {
        try {
            this.setState({ isExporting: true });

            if ('showSaveFilePicker' in window) {
                this.setState({ exportButtonText: 'Choose location...' });
                var date = new Date().toLocaleDateString('en-CA');
                var fileHandle = await (window as any).showSaveFilePicker({
                    suggestedName: `report_${date}.${this.state.format}`,
                    types: [
                        {
                            description: 'Exported File',
                            accept: { 'application/octet-stream': [`.${this.state.format}`] }
                        }
                    ]
                });

                this.handleExport(fileHandle);
            } else {
                // Fallback for unsupported browsers
                this.handleExport(null);
            }
        } catch (error) {
            console.error('File picker failed:', error);
            this.setState({
                isExporting: false,
                exportButtonText: 'Export Report'
            });
        }
    };

    handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ format: e.target.value });
    }

    private getFilenameFromContentDisposition = (contentDisposition?: string) => {
        if (!contentDisposition) return null;

        const fileNameStarMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
        if (fileNameStarMatch?.[1]) return decodeURIComponent(fileNameStarMatch[1].trim().replace(/"/g, ''));

        const fileNameMatch = /filename\s*=\s*("?)([^";]+)\1/i.exec(contentDisposition);
        if (fileNameMatch?.[2]) return fileNameMatch[2].trim();

        return null;
    };

    private getExtensionFromContentType = (contentType?: string) => {
        const ct = (contentType || '').toLowerCase();
        if (ct.includes('pdf')) return 'pdf';
        if (ct.includes('msword')) return 'doc';
        if (ct.includes('wordprocessingml.document')) return 'docx';
        if (ct.includes('spreadsheetml.sheet')) return 'xlsx';
        if (ct.includes('presentationml.presentation')) return 'pptx';
        if (ct.includes('text/plain')) return 'txt';
        return 'bin';
    };


    private handleNarrativeSaveAs = async () => {
        try {
            const reportId = this.state.reportId;
            if (!reportId) {
                console.error("No reportId available yet.");
                return;
            }

            this.setState({ isDownloadingNarrative: true, narrativeButtonText: 'Preparing...' });

            const resp = await getNarrativeReport(reportId);

            const contentType = String(resp.headers?.['content-type'] || 'application/octet-stream');
            const contentDisposition = resp.headers?.['content-disposition'] as string | undefined;
            const filenameFromHeader = this.getFilenameFromContentDisposition(contentDisposition);

            const ext = filenameFromHeader?.includes('.')
                ? filenameFromHeader.split('.').pop() || this.getExtensionFromContentType(contentType)
                : this.getExtensionFromContentType(contentType);

            const date = new Date().toLocaleDateString('en-CA');
            const suggestedName = filenameFromHeader || `narrative_report_${date}.${ext}`;

            const blob = new Blob([resp.data], { type: contentType });

            if ('showSaveFilePicker' in window) {
                this.setState({ narrativeButtonText: 'Choose location...' });

                const fileHandle = await (window as any).showSaveFilePicker({
                    suggestedName,
                    types: [
                        {
                            description: 'AI Narrative Report',
                            accept: {
                                [contentType]: [`.${ext}`],
                            },
                        },
                    ],
                });

                this.setState({ narrativeButtonText: 'Saving...' });

                const writable = await fileHandle.createWritable();
                await writable.write(blob);
                await writable.close();
            } else {
                // Fallback auto-download
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = suggestedName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(link.href);
            }
        } catch (error) {
            console.error('AI Narrative download failed:', error);
        } finally {
            this.setState({ isDownloadingNarrative: false, narrativeButtonText: 'Download AI Narrative' });
        }
    };



    public render() {
        return (
            <>
                <Container fluid className="d-flex flex-column h-100" style={{ width: "100%", height: "100%" }} >
                    <Row className="flex-grow-1">
                        <Col xs={12} className="px-0 d-flex justify-content-center flex-column h-100">
                            <div className="h-100">
                                {this.state.isLoading && (
                                    <section className="align-self-center text-center" id="pbidbLoading">
                                        <p>Initiating PowerBI Dashboard...</p>
                                        <FontAwesomeIcon icon={faSpinner} size="3x" spin={true} />
                                    </section>
                                )}
                                {!this.state.hasError && (
                                    <div id="powerBiReportContainer" ref={this.containerRef}
                                        style={{
                                            padding:"5px",
                                            height: this.state.calculatedHeight,
                                            width:"calc( 100% - 10px)",
                                            visibility: this.state.isLoading ? 'hidden' : 'visible', 
                                            opacity: this.state.isLoading ? 0 : 1, 
                                            transition: 'height 0.5s ease-in-out, width 0.5s ease-in-out, opacity 2s ease-in-out, visibility 0s linear 3s',
                                            willChange: 'height, width'
                                        }}
                                    >
                                    </div>
                                )}
                                {!this.state.isLoading && this.state.hasError && (
                                    <div className="p-2 align-self-center alert alert-danger">Initiate dashboard failed</div>
                                    )}
                            </div>
                        </Col>
                    </Row>
                    <Row className="flex-grow-0" style={{ flexBasis: 'auto' }}>
                        <Col className="d-flex align-items-center col-4 position-relative" style={{ "padding": "15px 15px 30px 15px" }} >
                            <label htmlFor="formatDropDown" className="me-2 flex-shrink-0 smaller-font lh-1 m-0">Export Format:</label>
                            <select className="form-select form-select-sm me-2 flex-grow-0 smaller-font lh-1 w-auto" id="formatDropDown"
                                onChange={this.handleFormatChange} value={this.state.format} disabled={this.state.isExporting || this.state.isLoading}>
                                <option value="pdf">PDF</option>
                                <option value="pptx">PPTX</option>
                            </select>
                            <button
                                onClick={this.handleSaveAs}
                                className="btn btn-sm btn-primary lh-1 me-2"
                                disabled={this.state.isExporting || this.state.isLoading || this.state.isDownloadingNarrative}
                            >
                                {this.state.exportButtonText}
                            </button>
                            <button
                                onClick={this.handleNarrativeSaveAs}
                                className="btn btn-sm btn-primary lh-1 me-2"
                                disabled={this.state.isLoading || this.state.isExporting || this.state.isDownloadingNarrative || !this.state.reportId}
                            >
                                {this.state.narrativeButtonText}
                            </button>
                            {(this.state.isExporting || this.state.isDownloadingNarrative) && (
                                <div className="spinner-overlay d-flex justify-content-center align-items-center">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }
}

export default PowerBIDashboard;
